import React, {useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import './ReviewerProductivityWidget.scss';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import {t} from 'i18next';
import {
  ReviewerProductivity,
  ReviewerProductivityWidget as ReviewerType,
} from '../../dashboardTypes';
import {useReviewerProductivity} from '../../../../hooks/dashboard/useDashboard';
import {CustomizeReviewerProductivityWidget} from '../customize-reviewer-productivity-widget/CustomizeReviewerProductivityWidget';
import {widgetIdMapping} from '../../dashboardUtils';

export const columns = [
  {key: 'completed', label: 'Completed'},
  {key: 'reviewTime', label: 'Review Time'},
  {key: 'docHr', label: 'Docs/Hr'},
];

interface ProductivityType {
  completed: number;
  reviewTime: string;
  docHr: number;
  productivityIncrease: boolean;
  name?: string;
}

interface ReviewerProductivityWidgetProps {
  showSettingsIcon?: boolean;
  onReplaceClick: () => void;
  reviewerProductivity?: ReviewerProductivity;
}

const ReviewerProductivityWidget: React.FC<ReviewerProductivityWidgetProps> = ({
  onReplaceClick,
  reviewerProductivity = {},
  showSettingsIcon = false,
}) => {
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const handleDrawer = (value: React.SetStateAction<boolean>) =>
    setIsCustomizeOpen(value);
  const [data, setData] = useState<ReviewerType>();
  const {
    data: reviewerProductivityData,
    isLoading: loading,
    isError: error,
  } = useReviewerProductivity({
    reviewerFilterOptionId: 5, //default value
    ...reviewerProductivity,
    widgetId: widgetIdMapping.reviewerProductivity,
  });

  useEffect(() => {
    setData(reviewerProductivityData);
  }, [reviewerProductivityData]);

  const onSuccess = (newData: ReviewerType) => {
    setData(newData);
  };

  const overallData = data
    ? {
        completed: data?.totalList[0]?.completedDoc,
        reviewTime: `${((data?.totalList[0]?.reviewDays ?? 0) * 24 + (data?.totalList[0]?.reviewHrs ?? 0)).toFixed(2)} Hrs`,
        docHr: data?.totalList[0]?.docsPerHour,
        productivityIncrease: (data?.totalList[0]?.variance ?? 0) > 0,
        name: '',
      }
    : undefined;

  const productivityData = data?.reviewerProductivityDataList
    ? data?.reviewerProductivityDataList.map((item) => ({
        completed: item?.completedDoc,
        reviewTime: `${((item?.reviewDays ?? 0) * 24 + (item?.reviewHrs ?? 0)).toFixed(2)} Hrs`,
        docHr: item?.docsPerHour,
        productivityIncrease: (item?.variance ?? 0) >= 0,
        name: item.reviewerName ?? '',
      }))
    : [];

  const reviewerLabel = useMemo(() => {
    let reviewerLabel = t('dashboard.reviewerProductivity.topReviewers');
    if (data?.selectedOption === 0) {
      reviewerLabel = t('dashboard.reviewerProductivity.specific');
    }
    if (data?.selectedOption === 6) {
      reviewerLabel = t('dashboard.reviewerProductivity.topReviewersHighest');
    }
    return reviewerLabel;
  }, [JSON.stringify(data)]);

  return (
    <ArrWidgetWrapper
      title={t('dashboard.reviewerProductivity.widgetTitle')}
      showInfo
      showReplace
      showSettings={showSettingsIcon}
      onReplaceClick={onReplaceClick}
      onSettingsClick={() => handleDrawer(true)}
      loading={loading}
      error={error}
      tooltipContent={t('dashboard.reviewerProductivity.tooltip')}
    >
      <CustomizeReviewerProductivityWidget
        showDrawer={isCustomizeOpen}
        handleDrawer={handleDrawer}
        initialFilter={{
          assignments: data?.filterData?.assignments ?? [],
          reviewers: data?.filterData?.reviewers ?? [],
          reviewerFilterOptionId: data?.filterData?.reviewerFilterOptionId ?? 0,
          fromDate: data?.filterData?.fromDate ?? null,
          toDate: data?.filterData?.toDate ?? null,
          widgetId: widgetIdMapping.reviewerProductivity,
        }}
        onSuccess={onSuccess}
      />
      <Box
        className="reviewer-productivity-root"
        data-testid="reviewer-productivity"
      >
        <Box className="overall-container">
          <Typography
            className="typo-text subhead5_size overall-text"
            data-testid={`reviewer-productivity-overall`}
          >
            {t('dashboard.reviewerProductivity.overall')}
          </Typography>
          <Box className="overall-data-header">
            {columns.map(({key, label}) => (
              <Box key={key} sx={{width: key === 'docHr' ? '20%' : '40%'}}>
                <Typography
                  className="overall-table-header"
                  data-testid={`reviewer-productivity-column-${label}`}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            className={`overall-data-row ${!data?.totalList[0] && 'show-empty-row'}`}
          >
            {overallData &&
              columns.map(({key}) => {
                const value = overallData[key as keyof ProductivityType];
                let styleForColumn = 'column-text';
                let showArrow = '';
                if (key === 'completed') {
                  styleForColumn = 'column-completed';
                } else if (key === 'docHr') {
                  styleForColumn = overallData.productivityIncrease
                    ? 'column-completed'
                    : 'column-warning';
                  showArrow = overallData.productivityIncrease
                    ? t('dashboard.reviewerProductivity.upArrow')
                    : t('dashboard.reviewerProductivity.downArrow');
                }

                return (
                  <Box
                    key={key}
                    sx={{width: key === 'docHr' ? '20%' : '40%'}}
                    data-testid={`overall-${key}`}
                  >
                    {data?.totalList[0] && (
                      <Typography
                        className={`column-text ${styleForColumn}`}
                        data-testid={`reviewer-productivity-value-${key}-value`}
                      >
                        {value}
                        {showArrow}
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>
        </Box>
        <Box className="top-reviewer-container">
          <Typography
            className="typo-text subhead5_size reviewer-text"
            data-testid={`reviewer-productivity-reviewer-label`}
          >
            {reviewerLabel}
          </Typography>
          <Box className="top-reviewer-header">
            {columns.map(({key, label}) => (
              <Box key={key} sx={{width: key === 'docHr' ? '20%' : '40%'}}>
                <Typography
                  className="top-reviewer-table-header"
                  data-testid={`reviewer-productivity-reviewer-label-value-${key}`}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {productivityData.map((reviewer, ind) => (
            <Box key={reviewer.name + ind.toLocaleString()}>
              <Typography
                className="reviewer-name"
                data-testid={`reviewer-productivity-name-${ind}`}
              >
                {reviewer.name}
              </Typography>
              <Box key={reviewer.name} className="reviewer-data-row">
                {columns.map(({key}) => {
                  const value = reviewer[key as keyof ProductivityType];
                  let styleForColumn = 'column-text';
                  let showArrow = '';
                  if (key === 'completed') {
                    styleForColumn = 'column-completed';
                  } else if (key === 'docHr') {
                    styleForColumn = reviewer.productivityIncrease
                      ? 'column-completed'
                      : 'column-warning';
                    showArrow = reviewer.productivityIncrease
                      ? t('dashboard.reviewerProductivity.upArrow')
                      : t('dashboard.reviewerProductivity.downArrow');
                  }

                  return (
                    <Box
                      key={key}
                      sx={{width: key === 'docHr' ? '20%' : '40%'}}
                      data-testid={`reviewer-${key}-${ind}`}
                    >
                      <Typography
                        className={`column-text ${styleForColumn}`}
                        data-testid={`reviewer-productivity-data-${key}-${ind}`}
                      >
                        {value}
                        {showArrow}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default ReviewerProductivityWidget;
